import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Pen,
  Eraser,
  MousePointer2,
  ImagePlus,
  Map as MapIcon,
  Type,
  Trash2,
  Download,
  Undo2,
} from 'lucide-react';

// ==================== TYPES ====================
type Mode = 'select' | 'draw' | 'erase';

interface Point {
  x: number;
  y: number;
}

interface Stroke {
  color: string;
  width: number;
  erase: boolean;
  points: Point[];
}

interface BoardNode {
  id: string;
  type: 'image' | 'map' | 'text';
  src: string; // data URL for image, embed URL for map, or text content
  x: number;
  y: number;
  w: number;
  h: number;
  color?: string; // text color
  fontSize?: number; // text size
}

// ==================== HELPERS ====================
let nodeCounter = 0;
const nextId = () => `node-${Date.now()}-${nodeCounter++}`;

// OpenStreetMap embed (no API key required)
const mapEmbedUrl =
  'https://www.openstreetmap.org/export/embed.html?bbox=-0.2%2C51.45%2C0.02%2C51.55&layer=mapnik';

// ==================== COMPONENT ====================
const DRAW: React.FC = () => {
  const [mode, setMode] = useState<Mode>('draw');
  const [color, setColor] = useState('#ef4444');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [nodes, setNodes] = useState<BoardNode[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const strokesRef = useRef<Stroke[]>([]);
  const drawingRef = useRef<Stroke | null>(null);

  // ----- canvas sizing + redraw -----
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const s of strokesRef.current) {
      if (s.points.length < 1) continue;
      ctx.save();
      ctx.globalCompositeOperation = s.erase ? 'destination-out' : 'source-over';
      ctx.strokeStyle = s.color;
      ctx.lineWidth = s.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      for (let i = 1; i < s.points.length; i++) {
        ctx.lineTo(s.points[i].x, s.points[i].y);
      }
      if (s.points.length === 1) {
        // single dot
        ctx.lineTo(s.points[0].x + 0.1, s.points[0].y + 0.1);
      }
      ctx.stroke();
      ctx.restore();
    }
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    redraw();
  }, [redraw]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  // ----- drawing handlers -----
  const pointerPos = (e: React.PointerEvent): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (mode !== 'draw' && mode !== 'erase') return;
    setSelectedId(null);
    (e.target as Element).setPointerCapture(e.pointerId);
    const stroke: Stroke = {
      color,
      width: mode === 'erase' ? strokeWidth * 4 : strokeWidth,
      erase: mode === 'erase',
      points: [pointerPos(e)],
    };
    drawingRef.current = stroke;
    strokesRef.current.push(stroke);
    redraw();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current.points.push(pointerPos(e));
    redraw();
  };

  const handlePointerUp = () => {
    drawingRef.current = null;
  };

  // ----- images -----
  const addImageFromDataUrl = useCallback((src: string) => {
    const img = new window.Image();
    img.onload = () => {
      const maxDim = 320;
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = img.width * scale;
      const h = img.height * scale;
      const container = containerRef.current;
      const cx = container ? container.clientWidth / 2 - w / 2 : 40;
      const cy = container ? container.clientHeight / 2 - h / 2 : 40;
      const id = nextId();
      setNodes((prev) => [
        ...prev,
        { id, type: 'image', src, x: Math.max(cx, 0), y: Math.max(cy, 0), w, h },
      ]);
      setSelectedId(id);
      setMode('select');
    };
    img.src = src;
  }, []);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      Array.from(files).forEach((file) => {
        if (!file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = () => addImageFromDataUrl(reader.result as string);
        reader.readAsDataURL(file);
      });
    },
    [addImageFromDataUrl]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  // paste images from clipboard
  useEffect(() => {
    const onPaste = (evt: Event) => {
      const e = evt as ClipboardEvent;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = () => addImageFromDataUrl(reader.result as string);
            reader.readAsDataURL(file);
          }
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addImageFromDataUrl]);

  // drag & drop files onto board
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const addMap = () => {
    const id = nextId();
    const container = containerRef.current;
    setNodes((prev) => [
      ...prev,
      {
        id,
        type: 'map',
        src: mapEmbedUrl,
        x: container ? Math.max(container.clientWidth / 2 - 250, 0) : 40,
        y: 60,
        w: 500,
        h: 350,
      },
    ]);
    setSelectedId(id);
    setMode('select');
  };

  const addText = () => {
    const id = nextId();
    const container = containerRef.current;
    setNodes((prev) => [
      ...prev,
      {
        id,
        type: 'text',
        src: 'Text',
        x: container ? Math.max(container.clientWidth / 2 - 100, 0) : 40,
        y: 120,
        w: 200,
        h: 60,
        color,
        fontSize: 20,
      },
    ]);
    setSelectedId(id);
    setEditingId(id);
    setMode('select');
  };

  // ----- node dragging & resizing -----
  const dragState = useRef<{
    id: string;
    action: 'move' | 'resize';
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
  } | null>(null);

  const startNodeDrag = (
    e: React.PointerEvent,
    node: BoardNode,
    action: 'move' | 'resize'
  ) => {
    if (mode !== 'select') return;
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    setSelectedId(node.id);
    dragState.current = {
      id: node.id,
      action,
      startX: e.clientX,
      startY: e.clientY,
      origX: node.x,
      origY: node.y,
      origW: node.w,
      origH: node.h,
    };
  };

  const onNodePointerMove = (e: React.PointerEvent) => {
    const ds = dragState.current;
    if (!ds) return;
    const dx = e.clientX - ds.startX;
    const dy = e.clientY - ds.startY;
    setNodes((prev) =>
      prev.map((n) => {
        if (n.id !== ds.id) return n;
        if (ds.action === 'move') {
          return { ...n, x: ds.origX + dx, y: ds.origY + dy };
        }
        return {
          ...n,
          w: Math.max(60, ds.origW + dx),
          h: Math.max(60, ds.origH + dy),
        };
      })
    );
  };

  const endNodeDrag = () => {
    dragState.current = null;
  };

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setNodes((prev) => prev.filter((n) => n.id !== selectedId));
    setSelectedId(null);
  }, [selectedId]);

  // delete/backspace to remove selected node
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, deleteSelected]);

  const undoStroke = () => {
    strokesRef.current.pop();
    redraw();
  };

  const clearDrawing = () => {
    strokesRef.current = [];
    redraw();
  };

  // ----- export board as PNG (drawing only; iframes can't be captured) -----
  const exportPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'osint-board.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // ==================== RENDER ====================
  const toolBtn = (active: boolean) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'bg-orange-500 text-white'
        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'
    }`;

  return (
    <div
      ref={containerRef}
      className="fixed top-0 bottom-0 left-72 right-0 xl:right-80 bg-white dark:bg-slate-900 overflow-hidden"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      onPointerMove={onNodePointerMove}
      onPointerUp={endNodeDrag}
    >
      {/* Toolbar */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex flex-wrap items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg px-3 py-2">
        <button className={toolBtn(mode === 'select')} onClick={() => setMode('select')}>
          <MousePointer2 className="w-4 h-4" /> Select
        </button>
        <button className={toolBtn(mode === 'draw')} onClick={() => setMode('draw')}>
          <Pen className="w-4 h-4" /> Draw
        </button>
        <button className={toolBtn(mode === 'erase')} onClick={() => setMode('erase')}>
          <Eraser className="w-4 h-4" /> Erase
        </button>

        <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 mx-1" />

        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="w-8 h-8 rounded cursor-pointer bg-transparent border border-slate-200 dark:border-slate-600"
          title="Stroke color"
        />
        <input
          type="range"
          min={1}
          max={30}
          value={strokeWidth}
          onChange={(e) => setStrokeWidth(Number(e.target.value))}
          className="w-24 accent-orange-500"
          title="Stroke width"
        />

        <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 mx-1" />

        <button className={toolBtn(false)} onClick={() => fileInputRef.current?.click()}>
          <ImagePlus className="w-4 h-4" /> Image
        </button>
        <button className={toolBtn(false)} onClick={addMap}>
          <MapIcon className="w-4 h-4" /> Map
        </button>
        <button className={toolBtn(false)} onClick={addText}>
          <Type className="w-4 h-4" /> Text
        </button>

        <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 mx-1" />

        <button className={toolBtn(false)} onClick={undoStroke} title="Undo last stroke">
          <Undo2 className="w-4 h-4" />
        </button>
        <button className={toolBtn(false)} onClick={clearDrawing} title="Clear drawing">
          <Trash2 className="w-4 h-4" />
        </button>
        <button className={toolBtn(false)} onClick={exportPng} title="Export drawing as PNG">
          <Download className="w-4 h-4" />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Image / map nodes layer */}
      {nodes.map((node) => {
        const selected = selectedId === node.id;
        return (
          <div
            key={node.id}
            className="absolute"
            style={{
              left: node.x,
              top: node.y,
              width: node.w,
              height: node.h,
              zIndex: selected ? 20 : 10,
            }}
          >
            <div
              className={`relative w-full h-full rounded-lg overflow-hidden ${
                selected ? 'ring-2 ring-orange-500' : ''
              } ${mode === 'select' && editingId !== node.id ? 'cursor-move' : ''}`}
              onPointerDown={(e) => {
                if (editingId === node.id) return; // let text editing capture the pointer
                startNodeDrag(e, node, 'move');
              }}
              onDoubleClick={() => {
                if (node.type === 'text' && mode === 'select') setEditingId(node.id);
              }}
            >
              {node.type === 'image' && (
                <img
                  src={node.src}
                  alt="board item"
                  className="w-full h-full object-contain pointer-events-none select-none"
                  draggable={false}
                />
              )}

              {node.type === 'map' && (
                <>
                  <iframe
                    title={`map-${node.id}`}
                    src={node.src}
                    className="w-full h-full border-0"
                    style={{ pointerEvents: mode === 'select' ? 'none' : 'auto' }}
                  />
                  {/* drag handle for the map (iframe swallows pointer events) */}
                  {mode === 'select' && <div className="absolute inset-0" />}
                </>
              )}

              {node.type === 'text' &&
                (editingId === node.id ? (
                  <textarea
                    autoFocus
                    value={node.src}
                    onChange={(e) =>
                      setNodes((prev) =>
                        prev.map((n) =>
                          n.id === node.id ? { ...n, src: e.target.value } : n
                        )
                      )
                    }
                    onBlur={() => setEditingId(null)}
                    className="w-full h-full resize-none bg-transparent outline-none p-1 leading-tight"
                    style={{ color: node.color, fontSize: node.fontSize }}
                  />
                ) : (
                  <div
                    className="w-full h-full p-1 whitespace-pre-wrap break-words leading-tight select-none"
                    style={{ color: node.color, fontSize: node.fontSize }}
                  >
                    {node.src || ' '}
                  </div>
                ))}
            </div>

            {/* controls when selected */}
            {selected && mode === 'select' && (
              <>
                <button
                  onClick={deleteSelected}
                  className="absolute -top-3 -right-3 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow z-30"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <div
                  onPointerDown={(e) => startNodeDrag(e, node, 'resize')}
                  className="absolute -bottom-2 -right-2 w-5 h-5 bg-orange-500 rounded-full cursor-nwse-resize z-30 border-2 border-white"
                  title="Resize"
                />
              </>
            )}
          </div>
        );
      })}

      {/* Drawing canvas (top layer; ignores pointer in select mode) */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{
          zIndex: 25,
          pointerEvents: mode === 'select' ? 'none' : 'auto',
          cursor: mode === 'erase' ? 'cell' : 'crosshair',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />

      {/* empty-state hint */}
      {nodes.length === 0 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-xs text-slate-400 dark:text-slate-500 pointer-events-none">
          Drop or paste images anywhere · Draw freehand · Add text or an interactive map
        </div>
      )}
    </div>
  );
};

export default DRAW;
